<template>
  <v-card>
    <v-form @submit.prevent="true" ref="form" v-model="valid" autocomplete="off">
      <v-container class="pt-3 pb-0">
        <h3 class="my-3">Fields and Responses</h3>
        <p v-if="value.clientId">
          Field: <strong>{{ value.parentQuestion.label }}</strong>
        </p>
        <v-alert type="error" dismissible v-if="errors">Please check errors in the form</v-alert>

        <v-col cols="6" class="py-1" v-if="value.clientId && answerTypesMap[value.parentQuestion.type].dependants">
          <a href="#" @click.prevent="addQuestion"
            >Add Field to <strong>{{ value.label }}</strong> Response</a
          >
        </v-col>
        <Question
          :question="question"
          v-for="question in questions"
          :key="question.clientId"
          @removeQuestion="removeQuestion"
          :value="value"
          :validate="performDOMValidation"
          @onDelete="onDelete"
        />
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="success" :disabled="!valid" @click="check">Save</v-btn>
        <v-btn @click="close">Close</v-btn>
      </v-card-actions>
    </v-form>
    <v-dialog persistent v-model="showDeleteConfirmationDialog" max-width="600">
      <v-card v-if="showDeleteConfirmationDialog">
        <v-container>
          <v-alert type="warning">
            You have deleted fields and/or responses, this will remove all cascade fields, responses, and responses relationships
            attached.
          </v-alert>

          <h4 class="pb-1" v-if="deleted.question">Deleted fields</h4>
          <div class="pb-1" v-for="question in deleted.question || []" :key="question.clientId">
            {{ question.label }}
            <v-tooltip bottom v-if="question.foundInRelationship">
              <template v-slot:activator="{ on }">
                <v-icon color="orange" dark v-on="on">mdi-delete-outline</v-icon>
              </template>
              <span>In Relationship</span>
            </v-tooltip>
          </div>
          <h4 class="pb-1" v-if="deleted.value">Deleted responses</h4>
          <div class="pb-1" v-for="value in deleted.value || []" :key="value.clientId">
            {{ value.label }}
            <v-tooltip bottom v-if="value.foundInRelationship">
              <template v-slot:activator="{ on }">
                <v-icon color="orange" dark v-on="on">mdi-delete-outline</v-icon>
              </template>
              <span>In Relationship</span>
            </v-tooltip>
          </div>
          <h3 class="py-3">Are you sure you want to continue with this change?</h3>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="save">Yes</v-btn>
            <v-btn color="black" text @click="cancel">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Question from '@/components/communityConfig/adminQA/Question'
import { AnswerTypes } from '@/common/types'

export default {
  name: 'QAForm',
  data() {
    return {
      questions: this.value.clientId ? this.$_.orderBy(this.value.questions, ['order'], ['asc']) : this.fields,
      errors: false,
      answerTypesMap: AnswerTypes.properties,
      showDeleteConfirmationDialog: false,
      deleted: {},
      relationshipsToDelete: [],
      valid: false
    }
  },
  methods: {
    addQuestion() {
      this.questions.push({
        clientId: new Date().getTime(),
        label: '',
        enabled: true,
        order: 1,
        type: '',
        values: []
      })
    },
    removeQuestion(question) {
      this.questions = this.questions.filter(q => q.clientId !== question.clientId)
    },
    save() {
      this.$refs.form.validate()
      if (this.validate().length) {
        this.errors = true
        return
      } else {
        this.errors = false
      }
      this.$emit('save', this.questions, this.relationshipsToDelete, this.deleted)
      this.deleted = {}
    },
    close() {
      this.deleted = {}
      this.$emit('close')
    },
    validate() {
      return this.questions.reduce((acc, cur) => {
        if (!cur.label || !cur.label.trim() || !String(cur.order) || !cur.type) {
          acc.push({ error: true, clientId: cur.clientId, type: 'question' })
        }
        const result = this.getQuestionsFromAnswers(cur.values)
        return acc.concat(result)
      }, [])
    },
    performDOMValidation() {
      if (this.$refs.form) this.$refs.form.validate()
    },
    getQuestionsFromAnswers(values = []) {
      return values.reduce((a, c) => {
        if (!c.label || !c.label.trim() || !String(c.order)) {
          a = a.concat({ error: true, clientId: c.clientId, type: 'answer' })
        }
        for (let cur of c.questions) {
          if (!cur.label || !cur.label.trim() || !String(cur.order) || !cur.type) {
            a = a.concat({ error: true, clientId: cur.clientId, type: 'question' })
          }
          a = a.concat(this.getQuestionsFromAnswers(cur.values))
        }
        return a
      }, [])
    },
    getChain(questions) {
      return questions.reduce((acc, cur) => {
        acc[cur.clientId] = {
          label: cur.label,
          type: 'question'
        }
        const result = this.getInnerChain(cur.values)
        acc = {
          ...acc,
          ...result
        }
        return acc
      }, {})
    },
    getInnerChain(values = []) {
      return values.reduce((a, c) => {
        if (!c.questions.length) {
          a[c.clientId] = {
            label: c.label,
            type: 'value'
          }
        }
        for (let cur of c.questions) {
          a[c.clientId] = {
            label: c.label,
            type: 'value'
          }
          a[cur.clientId] = {
            label: cur.label,
            type: 'question'
          }
          a = {
            ...a,
            ...this.getInnerChain(cur.values)
          }
        }
        return a
      }, {})
    },
    onDelete(item, type) {
      if (type === 'question' && item.type === AnswerTypes.TEXT) return
      if (!this.deleted[type]) {
        this.deleted[type] = []
      } 
      let chain = []
      if (type === 'question') {
        chain = this.getChain([item])
        chain[item.clientId] = { label: item.label, type: 'question' }
      } else {
        chain = this.getChain(item.questions)
        chain[item.clientId] = { label: item.label, type: 'value' }
      }

      for (let relationship of this.relationships) {
        for (let criterion of relationship.criteria) {
          if (chain[criterion.questionId] || chain[criterion.valueId]) {
            this.relationshipsToDelete.push({
              relationshipId: relationship.clientId,
              itemId: chain[criterion.questionId] ? criterion.questionId : criterion.valueId,
              type: chain[criterion.questionId] ? 'question' : 'value'
            })
          }
        }
      }

      this.deleted[type] = this.deleted[type]
        .filter(a => a.clientId !== item.clientId)
        .concat({
          label: item.label,
          clientId: item.clientId,
          foundInRelationship: this.relationshipsToDelete.findIndex(r => r.itemId === item.clientId) > -1
        })
    },
    cancel() {
      this.showDeleteConfirmationDialog = false
    },
    check() {
      if (Object.keys(this.deleted).length > 0) {
        this.showDeleteConfirmationDialog = true
        return
      }
      this.save()
    }
  },
  components: {
    Question
  },
  props: {
    fields: Array,
    value: Object,
    relationships: Array
  }
}
</script>
